import { useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { fetchDownload, type DownloadedFile } from "@utils/fileDownload";
import { parseSubscriptionStatus } from "@/models/organization";
import type {
    ApproveOrganizationResponse,
    CreateClientRequest,
    CreateClientResponse,
    CreateApprovedLocationRequest,
    LocationApprovalResult,
    OrganizationResponse,
    OrganizationDetail,
    UpdateOrganizationRequest,
    UpdateOrganizationResponse,
    UpdateOrganizationStatusRequest,
    GetOrganizationsQuery,
    LocationStatusCounts,
    PaginatedOrganizations,
    ResendOrganizationInvitationResponse,
} from "@/models/organization";
import {
    organizationsAtom,
    isProcessingOrganizationsAtom,
    organizationsListAtom,
    isFetchingOrganizationsListAtom,
    locationStatusCountsAtom,
    organizationDetailAtom,
    isFetchingOrganizationDetailAtom,
} from "@/store/organizationAtoms";

const ORGANIZATIONS_ENDPOINT = "/organizations";

// Which admin list / detail read may publish to its atoms (PR 369 review 1-2).
// Module-scope, like useProductApi's: the atoms are module-scope (the app
// mounts no Jotai Provider), so only a sequence answers "has a newer read
// taken over?". Without it a slower answer for client A, or for the previous
// filter, lands after the newer one and replaces it. Latest-only (`isLatest`).
const organizationListReads = createReadGuard();
const organizationReads = createReadGuard();
// The same for the customer's own organization (#407): the membership tab,
// checkout's bank-account probe and the payment panel all read it into
// organizationsAtom, so a slower, older answer could put back the card on file
// a save had just replaced. The publish watermark, as for the cart and the
// locations (`claimPublish`): a successful read publishes when nothing newer
// has published. So an older answer can't replace a newer one, and a good
// answer is not thrown away because a newer read then failed, which left the
// membership tab saying it couldn't find an organization that had loaded (PR
// 374 review 3).
const meOrganizationsReads = createReadGuard();

// The status-change failures the client detail words itself (review finding 4).
const STATUS_CHANGE_INLINE_FAILURES = [400, 403, 404, 409];
const isStatusChangeFailureExplainedInline = (
    status: number | undefined,
): boolean =>
    status !== undefined && STATUS_CHANGE_INLINE_FAILURES.includes(status);

// The client-edit failures the Edit Organization drawer words itself
// (Admin/Clients/_helpers/editOrganization.ts): the 409s, and a 404 the API
// authored (the client is gone). A 404 without the API's body (a gateway page,
// a missing route) says nothing about the client, so it keeps the toast.
const isEditFailureExplainedInline = (
    status: number | undefined,
    _errorCode: string | undefined,
    hasApiBody: boolean,
): boolean => status === 409 || (status === 404 && hasApiBody);

// The create-client failures the New Client drawer words itself
// (Admin/Clients/_helpers/createClient.ts): the 409s.
const isCreateClientFailureExplainedInline = (
    status: number | undefined,
): boolean => status === 409;

// The add-location failures the client detail words itself
// (Admin/Clients/_helpers/addLocation.ts): the 409 (a suspended or cancelled
// client) and a 404 the API authored (the client is gone). A validation 400
// (its toast carries the API's field messages), a 403, a 5xx, a 404 without
// the API's body and a network failure keep the toast.
const isAddLocationFailureExplainedInline = (
    status: number | undefined,
    _errorCode: string | undefined,
    hasApiBody: boolean,
): boolean => status === 409 || (status === 404 && hasApiBody);

export const useOrganizationApi = () => {
    const { callApi } = useApi();
    const organizations = useAtomValue(organizationsAtom);
    // Session-bound setters for what endSessionAtom clears; the in-flight
    // flags keep plain setters so their `finally` always lands.
    const setOrganizations = useSessionSetAtom(organizationsAtom);
    const setIsProcessing = useSetAtom(isProcessingOrganizationsAtom);
    const setOrganizationsList = useSessionSetAtom(organizationsListAtom);
    const setIsFetchingList = useSetAtom(isFetchingOrganizationsListAtom);
    const setStatusCounts = useSessionSetAtom(locationStatusCountsAtom);
    const setOrganizationDetail = useSessionSetAtom(organizationDetailAtom);
    const setIsFetchingDetail = useSetAtom(isFetchingOrganizationDetailAtom);

    // `silent` is for callers that read this lazily as a SECONDARY lookup and render their
    // own inline degradation (checkout's saved-bank-account probe): it suppresses the global
    // toast. The atom is still populated on success — that value is correct for everyone.
    // The payment wall's AddPaymentMethodPanel passes it too. No caller learns of a failure
    // from an atom: each keeps the read's outcome in its own state (#407).
    const getMeOrganizations = useCallback(async (
        { silent = false }: { silent?: boolean } = {},
    ): Promise<OrganizationResponse[]> => {
        const { claimPublish } = meOrganizationsReads.begin();

        const data = await callApi<OrganizationResponse[]>(
            `${ORGANIZATIONS_ENDPOINT}/me`,
            silent ? { skipErrorNotification: true } : {},
        );

        // subscription.status arrives as an integer, but MembershipStatusBadge
        // indexes a Record keyed by SubscriptionStatus — an unrecognized value
        // would resolve to undefined and throw on destructure. Normalize once,
        // at the boundary.
        const normalized = data.map((organization) =>
            organization.subscription
                ? {
                    ...organization,
                    subscription: {
                        ...organization.subscription,
                        status: parseSubscriptionStatus(
                            organization.subscription.status,
                        ),
                    },
                }
                : organization,
        );

        if (claimPublish()) {
            setOrganizations(normalized);
        }

        return normalized;
    }, [callApi, setOrganizations]);

    // Admin clients list (E5-S7 / US 194). Returns the paginated org list.
    const getOrganizationList = useCallback(
        async (
            query: GetOrganizationsQuery,
        ): Promise<PaginatedOrganizations> => {
            const { isLatest } = organizationListReads.begin();

            setIsFetchingList(true);

            try {
                const queryString = buildQueryString(query);
                const url = queryString
                    ? `${ORGANIZATIONS_ENDPOINT}?${queryString}`
                    : ORGANIZATIONS_ENDPOINT;
                const response =
                    await callApi<PaginatedOrganizations>(url);

                if (isLatest()) setOrganizationsList(response);

                return response;
            } finally {
                if (isLatest()) setIsFetchingList(false);
            }
        },
        [callApi, setOrganizationsList, setIsFetchingList],
    );

    // Global per-LOCATION status counts (GET /organizations/statuses —
    // repurposed by #326; approval is per-location, DecisionLog §1.2; #332).
    // Used by the admin top-bar badge. Independent of the list query, so it
    // never disturbs the Clients page data.
    const getOrganizationStatuses =
        useCallback(async (): Promise<LocationStatusCounts> => {
            const response = await callApi<LocationStatusCounts>(
                `${ORGANIZATIONS_ENDPOINT}/statuses`,
            );
            setStatusCounts(response);
            return response;
        }, [callApi, setStatusCounts]);

    const getMeOrganization = useCallback(
        (id: string): Promise<OrganizationResponse> =>
            callApi<OrganizationResponse>(`${ORGANIZATIONS_ENDPOINT}/me/${id}`),
        [callApi],
    );

    const getOrganization = useCallback(
        async (id: string): Promise<OrganizationDetail> => {
            const { isLatest } = organizationReads.begin();

            setIsFetchingDetail(true);

            try {
                const data = await callApi<OrganizationDetail>(
                    `${ORGANIZATIONS_ENDPOINT}/${id}`,
                );
                if (isLatest()) setOrganizationDetail(data);

                return data;
            } finally {
                if (isLatest()) setIsFetchingDetail(false);
            }
        },
        [callApi, setOrganizationDetail, setIsFetchingDetail],
    );

    // The client detail explains 400, 403, 404 and 409 inline
    // (Admin/Clients/_helpers/statusChange.ts), so only those skip the toast,
    // which would double up. Any other status still toasts, and so does a
    // network failure: it has no status, so the rule declines it.
    const updateOrganizationStatus = useCallback(
        async (
            id: string,
            data: UpdateOrganizationStatusRequest,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${ORGANIZATIONS_ENDPOINT}/${id}/status`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                    skipErrorNotification: isStatusChangeFailureExplainedInline,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Admin approve action (E5-S9 / #314). Charges + approves every PENDING
    // location on the org; re-POSTing is the retry (already-approved locations
    // are skipped server-side, so nobody is charged twice).
    // skipErrorNotification: the detail page renders its own inline, per-case
    // message (the two 409s, 404, 403), so the generic toast would double up.
    // `certificatesSeenAt` (PR 327 round 6): the shown detail's server-stamped
    // retrievedAt, passed through as given. A Pending location with a
    // certificate uploaded at or after it is not approved (its row says so),
    // so nothing is approved unseen. Absent: no query, the old behaviour.
    const approveOrganization = useCallback(
        async (
            id: string,
            certificatesSeenAt?: string,
        ): Promise<ApproveOrganizationResponse> => {
            setIsProcessing(true);

            try {
                const query = certificatesSeenAt
                    ? `?certificatesSeenAt=${encodeURIComponent(certificatesSeenAt)}`
                    : "";

                return await callApi<ApproveOrganizationResponse>(
                    `${ORGANIZATIONS_ENDPOINT}/${id}/approve${query}`,
                    { method: "POST", skipErrorNotification: true },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Admin "resend invitation" (#438 / OB-02): issues a new signup link,
    // kills every earlier one and emails the applicant. skipErrorNotification:
    // the client detail renders its own inline message per case (the two 409s,
    // 404), so the generic toast would double up.
    const resendOrganizationInvitation = useCallback(
        async (id: string): Promise<ResendOrganizationInvitationResponse> => {
            setIsProcessing(true);

            try {
                return await callApi<ResendOrganizationInvitationResponse>(
                    `${ORGANIZATIONS_ENDPOINT}/${id}/invitation/resend`,
                    { method: "POST", skipErrorNotification: true },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // GET /organizations/{organizationId}/files/{fileId} (#428). The container
    // is private and the endpoint needs the bearer token, so the bytes come
    // back through callApi as a Blob and are saved client-side — never linked
    // directly. "contracted", like the invoice download: an API-authored
    // 403/404 rejects as the ApiError for the row's inline sentence, every
    // other failure toasts and rejects as DownloadError. No accepted-type
    // list: the upload allow-list is configuration (pdf/doc/docx today) and
    // older rows predate it, so only an empty body or an HTML page is refused.
    const downloadOrganizationFile = useCallback(
        (organizationId: string, fileId: string): Promise<DownloadedFile> =>
            fetchDownload(
                callApi,
                `${ORGANIZATIONS_ENDPOINT}/${organizationId}/files/${fileId}`,
                { failures: "contracted" },
            ),
        [callApi],
    );

    // A platform admin edits a client (C5, API #426). The body goes as given:
    // its updatedAt is the detail read's own string, never re-serialized
    // through a Date. The Edit Organization drawer explains the API's own 404
    // and the 409s (organization_changed, email_in_use, email_registered)
    // inline, so only those skip the toast; anything else, a network failure
    // included, still toasts with the server's own words. A 200 without a
    // JSON body carries no answer (useApi hands back its text, "" when empty),
    // hence `| undefined`.
    const updateOrganization = useCallback(
        async (
            id: string,
            data: UpdateOrganizationRequest,
        ): Promise<UpdateOrganizationResponse | undefined> => {
            setIsProcessing(true);

            try {
                return await callApi<UpdateOrganizationResponse | undefined>(
                    `${ORGANIZATIONS_ENDPOINT}/${id}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                        skipErrorNotification: isEditFailureExplainedInline,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // A platform admin creates a client (C3, API PR 419): the organization,
    // its first location (approved by the certificate sent with it) and the
    // contact's signup invitation, as multipart. The New Client drawer
    // explains every 409 inline (email_in_use, email_registered,
    // tax_id_in_use), so only those skip the toast; anything else still
    // toasts: another 4xx in the server's words, a 5xx or a network failure
    // in a generic line of useApi's. A 201 without a JSON object carries no answer
    // (useApi hands back a body that is not JSON as its text, "" when empty),
    // so only an object is passed on, else `undefined` (PR 440 S1).
    const createClient = useCallback(
        async (
            data: CreateClientRequest,
        ): Promise<CreateClientResponse | undefined> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Names must match CreateClientCommandBody exactly (`Body.`
                // prefix, [FromForm]).
                formData.append("Body.Name", data.name);
                formData.append("Body.LegalBusinessName", data.legalBusinessName);
                formData.append("Body.TaxId", data.taxId);
                formData.append("Body.StreetAddress", data.streetAddress);
                formData.append("Body.City", data.city);
                formData.append("Body.State", data.state);
                formData.append("Body.ZipCode", data.zipCode);
                formData.append("Body.Country", data.country);
                formData.append("Body.FirstName", data.firstName);
                formData.append("Body.LastName", data.lastName);
                formData.append("Body.Email", data.email);
                formData.append("Body.PhoneNumber", data.phoneNumber);
                formData.append("Body.LocationName", data.locationName);
                formData.append(
                    "Body.LocationStreetAddress",
                    data.locationStreetAddress,
                );
                formData.append("Body.LocationCity", data.locationCity);
                formData.append("Body.LocationState", data.locationState);
                formData.append("Body.LocationZipCode", data.locationZipCode);
                formData.append("Body.LocationCountry", data.locationCountry);
                formData.append("Body.ResaleCertificate", data.resaleCertificate);
                // Absent reads as false on the API.
                if (data.confirmDuplicateTaxId) {
                    formData.append("Body.ConfirmDuplicateTaxId", "true");
                }

                const answer = await callApi<unknown>(ORGANIZATIONS_ENDPOINT, {
                    method: "POST",
                    body: formData,
                    contentType: "multipart",
                    skipErrorNotification: isCreateClientFailureExplainedInline,
                });

                return typeof answer === "object" && answer !== null
                    ? (answer as CreateClientResponse)
                    : undefined;
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // A platform admin adds a location to a client and approves it in the same
    // step, with its resale certificate (C4, API #427). The 201 says whether
    // it ended up approved: a client that already pays is charged at once,
    // and a charge that did not go through leaves it PendingPayment.
    const createApprovedLocation = useCallback(
        async (
            organizationId: string,
            data: CreateApprovedLocationRequest,
        ): Promise<LocationApprovalResult> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Names must match CreateApprovedLocationCommandBody exactly
                // (`Body.` prefix, [FromForm]).
                formData.append("Body.Name", data.name);
                formData.append("Body.StreetAddress", data.streetAddress);
                formData.append("Body.City", data.city);
                formData.append("Body.State", data.state);
                formData.append("Body.ZipCode", data.zipCode);
                formData.append("Body.Country", data.country);
                formData.append("Body.ResaleCertificate", data.resaleCertificate);

                return await callApi<LocationApprovalResult>(
                    `${ORGANIZATIONS_ENDPOINT}/${organizationId}/approved-locations`,
                    {
                        method: "POST",
                        body: formData,
                        contentType: "multipart",
                        skipErrorNotification:
                            isAddLocationFailureExplainedInline,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const deleteOrganization = useCallback(
        async (id: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${ORGANIZATIONS_ENDPOINT}/${id}`, {
                    method: "DELETE",
                });

                const updatedOrganizations = organizations.filter(
                    (org) => org.id !== id,
                );

                setOrganizations(updatedOrganizations);
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, organizations, setOrganizations],
    );

    return {
        getMeOrganizations,
        getMeOrganization,
        getOrganizationList,
        getOrganizationStatuses,
        getOrganization,
        approveOrganization,
        resendOrganizationInvitation,
        updateOrganizationStatus,
        downloadOrganizationFile,
        updateOrganization,
        createClient,
        createApprovedLocation,
        deleteOrganization,
    };
};

// Serializes a query object to a query string. Arrays expand to repeated keys
// (?searchBy=1&searchBy=3); undefined/null values are skipped.
const buildQueryString = (params: GetOrganizationsQuery): string => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === "" || value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
        } else {
            searchParams.append(key, String(value));
        }
    });

    return searchParams.toString();
};
